import DeleteIcon from '@mui/icons-material/Delete'
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import type { ChangeEvent } from 'react'
import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Footer } from '../components/Footer'
import { ModalConfirmDialog } from '../components/ModalConfirmDialog'
import { loadStatus } from '../constants/loadStatus'
import { errorMsg } from '../constants/messages'
import { useAuth } from '../hooks/useAuth'
import { useC213DeviceGet } from '../hooks/useC213DeviceGet'
import { useC214DeviceMemoUpdate } from '../hooks/useC214DeviceMemoUpdate'
import { useC215DeviceDelete } from '../hooks/useC215DeviceDelete'
import { useC212DeviceImport } from '../hooks/useC212DeviceImport'
import { useC902SignedUrlGet } from '../hooks/useC902SignedUrlGet'
import { useCloudStorage } from '../hooks/useCloudStorage'
import { validateImportFileSize } from '../util/FileSize'
import { LanguageContext } from '../util/LanguageContext'

const zipPattern = /.(zip)$/i

type TenantDevice = {
  device_id: string
  cotopat_id?: string
  device_type?: string
  memo?: string
  start_date?: number
  end_date?: number
  license_type?: string
  is_license_active?: boolean
  rhq?: string
  sales_company?: string
}

type TenantDeviceListResponse = {
  tenant?: string
  devices?: TenantDevice[]
}

export const DeviceList = () => {
  const auth = useAuth()
  const navigate = useNavigate()
  const translation = useContext(LanguageContext)

  const c213 = useC213DeviceGet()
  const c214 = useC214DeviceMemoUpdate()
  const c215 = useC215DeviceDelete()
  const c212 = useC212DeviceImport()
  const c902 = useC902SignedUrlGet()
  const cs = useCloudStorage()

  const [devices, setDevices] = useState<TenantDevice[]>([])
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<string[]>([])
  const [memoMap, setMemoMap] = useState<Record<string, string>>({})
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [openErrorDialog, setOpenErrorDialog] = useState(false)
  const [errorDialogText, setErrorDialogText] = useState('')

  const fileInputRef = useRef<HTMLInputElement>(null)

  const loading =
    c213.loading === loadStatus.LOADING ||
    c214.loading === loadStatus.LOADING ||
    c215.loading === loadStatus.LOADING ||
    c212.loading === loadStatus.LOADING ||
    c902.loading === loadStatus.LOADING ||
    cs.loading === loadStatus.LOADING

  useEffect(() => {
    auth.setIsDisable(loading)
  }, [auth, loading])

  const refreshDevices = async () => {
    const result = await c213.execDeviceGet(auth.tenant)
    if (!result.success) {
      setErrorDialogText(result.message ?? errorMsg.SYSTEM_ERROR)
      setOpenErrorDialog(true)
      return
    }

    const responseData = result.info as TenantDevice[] | TenantDeviceListResponse | undefined
    const rawDevices = Array.isArray(responseData) ? responseData : (responseData?.devices ?? [])
    const resultDevices: TenantDevice[] = rawDevices
      .filter((d): d is TenantDevice => !!d.device_id)
      .map(d => ({ ...d, device_id: d.device_id }))

    setDevices(resultDevices)
    setMemoMap(
      resultDevices.reduce<Record<string, string>>((acc, device) => {
        acc[device.device_id] = device.memo ?? ''
        return acc
      }, {}),
    )
    setSelectedDeviceIds(prev =>
      prev.filter(id => resultDevices.some(device => device.device_id === id)),
    )
  }

  useEffect(() => {
    refreshDevices()
  }, [])

  const formatYmdByLocale = (
    unixSeconds: number | null | undefined,
    locale: string,
    fallback: string
  ) => {
    if (unixSeconds == null) return fallback
    const d = new Date(unixSeconds * 1000) // 秒 → ms
    // Invalid Date 対策
    if (Number.isNaN(d.getTime())) return fallback

    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(d)
  }

  const parsedRows = useMemo(() => {
    const userLanguage = navigator.language || navigator.languages?.[0] || 'en-US'

    return devices.map(device => {
      const normalizedDeviceType = (device.device_type ?? '').toLowerCase()
      const deviceType =
        normalizedDeviceType === 'screen'
          ? 'Screen'
          : normalizedDeviceType === 'mobile'
            ? 'Mobile'
            : (device.device_type ?? '')

      const isLicenseActive = device.is_license_active !== false
      const unknown = translation['label_contract_period_unknown']

      const startDate = formatYmdByLocale(device.start_date, userLanguage, unknown)
      const endDate = formatYmdByLocale(device.end_date, userLanguage, unknown)

      let contractPeriod = unknown
      if (device.license_type === 'subscription') {
        if (endDate === unknown) {
          contractPeriod = translation['label_contract_period_subscription']
        } else {
          contractPeriod = translation['label_end_date'] + `: ${endDate}`
        }
      } else if (device.license_type === 'term') {
        contractPeriod = `${startDate} - ${endDate}`
      }
      if (!isLicenseActive) {
        contractPeriod = translation['label_license_not_assigned']
      }

      return {
        ...device,
        deviceType,
        contractPeriod,
        isLicenseActive,
      }
    })
  }, [devices, translation])

  const allSelected = parsedRows.length > 0 && selectedDeviceIds.length === parsedRows.length

  const handleToggleAll = () => {
    if (allSelected) {
      setSelectedDeviceIds([])
      return
    }
    setSelectedDeviceIds(parsedRows.map(row => row.device_id))
  }

  const handleToggleOne = (deviceId: string) => {
    setSelectedDeviceIds(prev =>
      prev.includes(deviceId) ? prev.filter(id => id !== deviceId) : [...prev, deviceId],
    )
  }

  const handleDelete = async () => {
    setDeleteDialogOpen(false)
    if (selectedDeviceIds.length === 0) return

    const results = await Promise.all(
      selectedDeviceIds.map(async deviceId => {
        const result = await c215.execDeviceDelete(auth.tenant, deviceId)
        return { deviceId, result }
      }),
    )

    const failed = results.filter(r => !r.result.success)
    if (failed.length > 0) {
      const lines = failed.map(f => {
        const cotopatId = devices.find(d => d.device_id === f.deviceId)?.cotopat_id ?? f.deviceId
        const msg = f.result.message ? (translation[f.result.message] ?? errorMsg.SYSTEM_ERROR) : errorMsg.SYSTEM_ERROR
        return translation['label_cotopat_id'] + `: ${cotopatId} - ${msg}`
      })
      setErrorDialogText(lines.join('\n'))
      setOpenErrorDialog(true)
    } else {
      setSelectedDeviceIds([])
    }
    await refreshDevices()
  }

  const executeImport = async (targetDeviceIds: string[], file: File) => {
    for (const deviceId of targetDeviceIds) {
      const signedUrlResult = await c902.execSignedUrlGet(auth.tenant)
      if (!signedUrlResult.success) {
        throw new Error(signedUrlResult.message ?? errorMsg.SYSTEM_ERROR)
      }

      const uploadResult = await cs.execUploadFile(signedUrlResult.message!, file)
      if (!uploadResult.success) {
        throw new Error(uploadResult.message ?? errorMsg.SYSTEM_ERROR)
      }

      const importResult = await c212.execDeviceImport(
        deviceId,
        signedUrlResult.message!,
        auth.tenant,
      )
      if (!importResult.success) {
        throw new Error(importResult.message ?? errorMsg.SYSTEM_ERROR)
      }
    }
  }

  const handleImportSelected = () => {
    if (selectedDeviceIds.length === 0) {
      return
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
    fileInputRef.current?.click()
  }

  const handleImportFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) {
      return
    }

    if (!validateImportFileSize(file)) {
      setErrorDialogText(errorMsg.IMPORT_FILE_SIZE_ERROR)
      setOpenErrorDialog(true)
      return
    }

    if (!zipPattern.test(file.name)) {
      setErrorDialogText(errorMsg.UNSUPPORTED_FILE_ERROR)
      setOpenErrorDialog(true)
      return
    }

    try {
      await executeImport(selectedDeviceIds, file)
      await refreshDevices()
    } catch (error) {
      if (error instanceof Error) {
        setErrorDialogText(error.message)
        setOpenErrorDialog(true)
      }
    }
  }

  const handleMemoChange = (deviceId: string, value: string) => {
    setMemoMap(prev => ({ ...prev, [deviceId]: value }))
  }

  const handleMemoBlur = async (deviceId: string) => {
    const originalMemo = devices.find(device => device.device_id === deviceId)?.memo ?? ''
    const normalizedMemo = (memoMap[deviceId] ?? '').normalize('NFC')
    const currentMemo = normalizedMemo.length > 255 ? normalizedMemo.slice(0, 255) : normalizedMemo

    if (currentMemo === originalMemo) {
      return
    }

    const result = await c214.execDeviceMemoUpdate(deviceId, currentMemo)
    if (!result.success) {
      setErrorDialogText(result.message ?? errorMsg.SYSTEM_ERROR)
      setOpenErrorDialog(true)
      return
    }

    setDevices(prev =>
      prev.map(device =>
        device.device_id === deviceId ? { ...device, memo: currentMemo } : device,
      ),
    )
    setMemoMap(prev => ({ ...prev, [deviceId]: currentMemo }))
  }

  const goToCotopatWeb = (deviceId: string) => {
    auth.setDeviceId(deviceId)
    const device = devices.find(d => d.device_id === deviceId)
    auth.setRhq(device?.rhq ?? '')
    auth.setSalesCompany(device?.sales_company ?? '')
    navigate(`/phrase?deviceId=${encodeURIComponent(deviceId)}`)
  }

  const headerDividerSx = {
    position: 'relative',
    '&::after': {
      content: '""',
      position: 'absolute',
      right: 0,
      top: '25%',
      height: '50%',
      borderRight: '1px solid #ccc',
    },
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        backgroundColor: '#ececec',
        pb: 8,
      }}
    >
      {openErrorDialog && (
        <ModalConfirmDialog
          open={openErrorDialog}
          text={errorDialogText}
          onClick={() => setOpenErrorDialog(false)}
        />
      )}

      {loading && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.35)',
            pointerEvents: 'auto',
            cursor: 'wait',
            zIndex: 1500,
          }}
        >
          <CircularProgress />
        </Box>
      )}

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{translation['button_delete']}</DialogTitle>
        <DialogContent>
          <Typography>{translation['confirmation_delete']}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>{translation['button_cancel']}</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            {translation['button_delete']}
          </Button>
        </DialogActions>
      </Dialog>

      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept=".zip"
        onChange={handleImportFileChange}
      />

      <Box sx={{ px: { xs: 3, md: 6 }, pt: 2 }}>
        <Typography sx={{ font: 'normal normal bold 34px/40px Noto Sans JP', color: '#202020' }}>
          Cotopat
        </Typography>
      </Box>

      <Box sx={{ px: { xs: 3, md: 6 }, pt: 2, maxWidth: 1160, mx: 'auto' }}>
        <Typography sx={{ font: 'normal normal bold 28px/36px Noto Sans JP', color: '#202020' }}>
          {translation['label_device_list_title']}
        </Typography>
        <Typography sx={{ mt: 1, font: 'normal normal normal 0.875rem/1.5 Noto Sans JP', color: '#202020' }}>
          {translation['message_device_list_description']}
        </Typography>
        <Typography sx={{ font: 'normal normal normal 0.875rem/1.5 Noto Sans JP', color: '#202020' }}>
          {translation['message_device_list_description2']}
        </Typography>

        <Paper
          elevation={0}
          sx={{
            mt: 4,
            p: { xs: 2, md: 4 },
            borderRadius: '12px',
            backgroundColor: '#f7f7f7',
          }}
        >
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <Button
              startIcon={<DeleteIcon />}
              variant="contained"
              disabled={selectedDeviceIds.length === 0 || loading}
              onClick={() => setDeleteDialogOpen(true)}
              sx={{
                backgroundColor: '#17a2c4',
                color: '#fff',
                borderRadius: '8px',
                '&:hover': { backgroundColor: '#1594b2' },
              }}
            >
              {translation['button_delete_device']}
            </Button>

            <Button
              startIcon={<FileUploadOutlinedIcon />}
              variant="contained"
              disabled={selectedDeviceIds.length === 0 || loading}
              onClick={handleImportSelected}
              sx={{
                backgroundColor: '#17a2c4',
                color: '#fff',
                borderRadius: '8px',
                '&:hover': { backgroundColor: '#1594b2' },
              }}
            >
              {translation['button_import_data']}
            </Button>
          </Box>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{ width: 56, borderBottom: '2px solid #3e3e3e', ...headerDividerSx }}
                  >
                    <Checkbox
                      checked={allSelected}
                      onChange={handleToggleAll}
                      inputProps={{ 'aria-label': translation['label_select_all_devices'] }}
                    />
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: 700, borderBottom: '2px solid #3e3e3e', ...headerDividerSx }}
                  >
                    {translation['label_device_type']}
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: 700, borderBottom: '2px solid #3e3e3e', ...headerDividerSx }}
                  >
                    {translation['label_cotopat_id']}
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: 700, borderBottom: '2px solid #3e3e3e', ...headerDividerSx }}
                  >
                    {translation['label_contract_period']}
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: 700, borderBottom: '2px solid #3e3e3e', ...headerDividerSx }}
                  >
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                      <Box component="span">{translation['label_memo_input']}</Box>
                      <Tooltip
                        title={translation['label_memo_description']}
                        arrow
                      >
                        <InfoOutlinedIcon sx={{ fontSize: 18, color: '#17a2c4', cursor: 'help' }} />
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, borderBottom: '2px solid #3e3e3e' }}>
                    {translation['label_transition_web']}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {parsedRows.map(row => {
                  const memoValue = memoMap[row.device_id] ?? ''
                  const rowTextColor = row.isLicenseActive ? '#202020' : '#d32f2f'
                  const displayCotopatId = row.cotopat_id || row.device_id

                  return (
                    <TableRow key={row.device_id}>
                      <TableCell>
                        <Checkbox
                          checked={selectedDeviceIds.includes(row.device_id)}
                          onChange={() => handleToggleOne(row.device_id)}
                        />
                      </TableCell>
                      <TableCell sx={{ color: rowTextColor }}>{row.deviceType}</TableCell>
                      <TableCell sx={{ color: rowTextColor }}>{displayCotopatId}</TableCell>
                      <TableCell sx={{ color: rowTextColor }}>
                        <Box>{row.contractPeriod}</Box>
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          value={memoValue}
                          onChange={event => {
                            if (!row.isLicenseActive) {
                              return
                            }
                            handleMemoChange(row.device_id, event.target.value)
                          }}
                          onBlur={() => {
                            void handleMemoBlur(row.device_id)
                          }}
                          inputProps={{
                            maxLength: 255,
                            readOnly: !row.isLicenseActive,
                          }}
                          placeholder={translation['label_memo_placeholder']}
                          sx={{
                            width: 240,
                            '& .MuiInputBase-input': {
                              color: row.isLicenseActive ? 'inherit' : '#d32f2f',
                              width: '20ch',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            },
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          disabled={!row.isLicenseActive}
                          onClick={() => goToCotopatWeb(row.device_id)}
                        >
                          <OpenInNewIcon
                            sx={{ color: row.isLicenseActive ? '#17a2c4' : 'action.disabled' }}
                          />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>

      <Footer />
    </Box>
  )
}
